/**
 * Hook for managing saved configuration templates
 */

import { useState, useEffect, useCallback } from 'react';
import { DatacenterConfig } from '../types/configurator';

const STORAGE_KEY = 'hermit-config-templates';
const MAX_TEMPLATES = 10;

export interface ConfigTemplate {
  id: string;
  name: string;
  description: string;
  config: DatacenterConfig;
  createdAt: number;
  updatedAt: number;
}

interface TemplateStorage {
  templates: ConfigTemplate[];
  version: number;
}

function generateId(): string {
  return `tpl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function loadTemplates(): ConfigTemplate[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed: TemplateStorage = JSON.parse(stored);
      return parsed.templates || [];
    }
  } catch {
    // Ignore parse errors
  }
  return [];
}

function saveTemplates(templates: ConfigTemplate[]): void {
  try {
    const storage: TemplateStorage = {
      templates: templates.slice(0, MAX_TEMPLATES),
      version: 1,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
  } catch {
    // Ignore storage errors
  }
}

export function useConfigTemplates() {
  const [templates, setTemplates] = useState<ConfigTemplate[]>(() => loadTemplates());

  // Sync to localStorage
  useEffect(() => {
    saveTemplates(templates);
  }, [templates]);

  const saveTemplate = useCallback(
    (name: string, description: string, config: DatacenterConfig): ConfigTemplate => {
      const template: ConfigTemplate = {
        id: generateId(),
        name,
        description,
        config: JSON.parse(JSON.stringify(config)), // Deep clone
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      setTemplates(prev => [template, ...prev].slice(0, MAX_TEMPLATES));
      return template;
    },
    []
  );

  const updateTemplate = useCallback(
    (id: string, updates: Partial<Pick<ConfigTemplate, 'name' | 'description' | 'config'>>) => {
      setTemplates(prev =>
        prev.map(t =>
          t.id === id
            ? {
                ...t,
                ...updates,
                config: updates.config ? JSON.parse(JSON.stringify(updates.config)) : t.config,
                updatedAt: Date.now(),
              }
            : t
        )
      );
    },
    []
  );

  const deleteTemplate = useCallback((id: string) => {
    setTemplates(prev => prev.filter(t => t.id !== id));
  }, []);

  const getTemplate = useCallback(
    (id: string): ConfigTemplate | undefined => {
      return templates.find(t => t.id === id);
    },
    [templates]
  );

  const exportTemplates = useCallback((): string => {
    return JSON.stringify({ templates, exportedAt: new Date().toISOString() }, null, 2);
  }, [templates]);

  const importTemplates = useCallback((json: string): number => {
    try {
      const data = JSON.parse(json);
      if (data.templates && Array.isArray(data.templates)) {
        const imported = data.templates.filter(
          (t: ConfigTemplate) => t.id && t.name && t.config
        );
        setTemplates(prev => {
          // Merge, avoiding duplicates by id
          const existing = new Set(prev.map(t => t.id));
          const newTemplates = imported.filter((t: ConfigTemplate) => !existing.has(t.id));
          return [...newTemplates, ...prev].slice(0, MAX_TEMPLATES);
        });
        return imported.length;
      }
    } catch {
      return 0;
    }
    return 0;
  }, []);

  return {
    templates,
    saveTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplate,
    exportTemplates,
    importTemplates,
    maxTemplates: MAX_TEMPLATES,
  };
}
